// ─────────────────────────────────────────────────────────────────────────────
// OUTFORM — CONCEPT 3: REVIEW QUEUE
//
// Atlas draft, 25 Aug 2026. Replaces the earlier "docked chat + inline Cmd-K
// popover" version of this file (24 Aug) — a design conversation between Erez
// and Atlas moved past that idea. The new premise:
//
//   The agent never holds a conversation. It works the document directly and
//   never narrates in sentences. Instead it drops discrete, numbered
//   suggestions into a side QUEUE — like Google Docs' Suggesting mode or a
//   code-review diff, not a chatbot. Each suggestion is tied to a specific
//   cell: what changed, the before/after value, a one-line reason. Two
//   buttons — Accept, Reject — no reply required to operate it.
//
// The sheet carries the suggestion inline too (a left-border tint + a small
// numbered badge on the affected row), so the card and the cell are always
// the same thing seen from two places. Accepted cards leave a small comment
// icon on the cell as a permanent provenance mark; rejected cards leave the
// cell untouched but the decision — and the reason — stays in the record.
//
// A DIRECT EDIT (the estimator typing into a field themselves) is visually
// distinct from a SUGGESTION (the agent's own idea) — that distinction is
// the whole second half of the story: a human edit is what tells the agent
// to look again, and it re-opens the queue with new cards instead of asking
// a question in a thread.
//
// ── WHAT THIS IS, PLAINLY ────────────────────────────────────────────────
// A click-through visual demo, not a narrated video, not a working app. Play
// advances on a fixed timer; Prev/Next/dots are the scrub controls; the
// caption under the controls is plain text. The Accept/Reject/Ask buttons on
// a card are illustrative — the story advances via the deck controls, the
// same as every other concept in this comparison.
//
// ── BOUNDARIES ───────────────────────────────────────────────────────────
// This file imports NOTHING from board/outform/ (that tree is Quinn's, for
// Outform's real board) and nothing outside this file imports FROM here. The
// mock sheet below is a styled grid, not the real Univer library — this is a
// design comparison, not a build. Own CSS, own prefix (`.oc3-`), so nothing
// can collide with Quinn's `.of-*` classes.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import { animate, stagger } from "animejs";
import "./outform-concept3.css";

const money = (n: number) => "$" + Math.round(n).toLocaleString("en-US");

// ── the job ──────────────────────────────────────────────────────────────
const UNITS = 340;

const ASSEMBLY_AFTER = 38.2;
const BASE_RATE = 30.0;
const BASE_PROPOSED = 31.9;
const FABRICATION_BEFORE = 34.0;
const FABRICATION_AFTER = 31.5;
const FINISHING_BEFORE = 19.25;
const FINISHING_AFTER = 17.75;

const STATIC_RATES: Record<string, number> = {
  qc: 8.4,
  install: 6.1,
  acrylic: 41.9,
  led: 22.15,
  hardware: 3.6,
};

const MARGIN_BASE = 0.5;
const MARGIN_MID = 0.55;
const MARGIN_FINAL = 0.58;
const MARGIN_FLOOR = 0.5;
const PRICE_PUSHBACK = 137000;

type ItemRow = { kind: "item"; id: string; label: string; qty: number; unit: string };
type RowDef = { kind: "colhead" } | { kind: "section"; label: string } | ItemRow;

const SHEET_ROWS: RowDef[] = [
  { kind: "colhead" },
  { kind: "section", label: "Labor" },
  { kind: "item", id: "assembly", label: "Assembly labor", qty: UNITS, unit: "hrs" },
  { kind: "item", id: "fabrication", label: "Fabrication labor", qty: UNITS, unit: "hrs" },
  { kind: "item", id: "finishing", label: "Finishing labor", qty: UNITS, unit: "hrs" },
  { kind: "item", id: "qc", label: "QC & inspection", qty: UNITS, unit: "hrs" },
  { kind: "item", id: "install", label: "Install labor", qty: UNITS, unit: "hrs" },
  { kind: "section", label: "Materials" },
  { kind: "item", id: "base", label: "ABS moulded base", qty: UNITS, unit: "ea" },
  { kind: "item", id: "acrylic", label: "Acrylic panel, edge-lit", qty: UNITS, unit: "ea" },
  { kind: "item", id: "led", label: "LED module + driver", qty: UNITS, unit: "ea" },
  { kind: "item", id: "hardware", label: "Hardware & fasteners", qty: UNITS, unit: "ea" },
];

// rates arrive progressively as the deck advances — this is the "agent works
// silently on the document" behaviour, scripted per step rather than truly
// computed, same technique the sibling concepts use for their own state.
function rateFor(id: string, step: number): number | null {
  if (step < 4) return null;
  switch (id) {
    case "assembly":
      return step >= 7 ? ASSEMBLY_AFTER : null; // blank until the suggestion is accepted
    case "base":
      return BASE_RATE; // the proposed change is rejected — this cell never moves
    case "fabrication":
      return step >= 13 ? FABRICATION_AFTER : FABRICATION_BEFORE;
    case "finishing":
      return step >= 13 ? FINISHING_AFTER : FINISHING_BEFORE;
    default:
      return STATIC_RATES[id] ?? null;
  }
}

function totalCost(step: number): number {
  return SHEET_ROWS.reduce((sum, r) => {
    if (r.kind !== "item") return sum;
    const rate = rateFor(r.id, step);
    return sum + (rate == null ? 0 : r.qty * rate);
  }, 0);
}

function marginTarget(step: number): number {
  if (step >= 10) return MARGIN_FINAL;
  if (step >= 9) return MARGIN_MID;
  return MARGIN_BASE;
}

function computePrice(step: number): number {
  if (step >= 12) return PRICE_PUSHBACK; // the client's own number, typed in directly
  const cost = totalCost(step);
  const target = marginTarget(step);
  return cost / (1 - target);
}

// ── the suggestion queue ─────────────────────────────────────────────────
type SugStatus = "pending" | "accepted" | "rejected";
type Suggestion = {
  id: number;
  rowId: string | null; // null = tied to a summary field (margin), not a grid row
  title: string;
  before: string;
  after: string;
  reason: string;
  rejectReason?: string;
};

const SUGGESTIONS: Suggestion[] = [
  {
    id: 1,
    rowId: "assembly",
    title: "Assembly labor rate",
    before: "—",
    after: `$${ASSEMBLY_AFTER.toFixed(2)}/hr`,
    reason: "Based on 3 comparable jobs, averaging $38.03/hr for this display type.",
  },
  {
    id: 2,
    rowId: "base",
    title: "ABS moulded base — unit cost",
    before: `$${BASE_RATE.toFixed(2)}/ea`,
    after: `$${BASE_PROPOSED.toFixed(2)}/ea`,
    reason: "Vendor quote updated Aug 12 — this line is still on the old catalog rate.",
    rejectReason: "Their quote arrived after we priced this job — hold the old rate until the PO confirms it.",
  },
  {
    id: 3,
    rowId: "margin",
    title: "Target margin",
    before: `${Math.round(MARGIN_BASE * 100)}%`,
    after: `${Math.round(MARGIN_MID * 100)}%`,
    reason: "Comparable endcap jobs run 53–58% — 55% sits mid-band.",
  },
  {
    id: 4,
    rowId: "margin",
    title: "Margin adjustment — repeat client",
    before: `${Math.round(MARGIN_MID * 100)}%`,
    after: `${Math.round(MARGIN_FINAL * 100)}%`,
    reason: "Nordstrom has 2 prior jobs that closed under-margin — a 3-point bump offsets that pattern.",
  },
  {
    id: 5,
    rowId: "fabrication",
    title: "Fabrication labor rate",
    before: `$${FABRICATION_BEFORE.toFixed(2)}/hr`,
    after: `$${FABRICATION_AFTER.toFixed(2)}/hr`,
    reason: "Trims cost to protect margin at the new target price without touching assembly or materials.",
  },
  {
    id: 6,
    rowId: "finishing",
    title: "Finishing labor rate",
    before: `$${FINISHING_BEFORE.toFixed(2)}/hr`,
    after: `$${FINISHING_AFTER.toFixed(2)}/hr`,
    reason: "Same pass — brings blended labor in line with the revised target.",
  },
];

// when a card LANDS in the queue, and the step at which it RESOLVES (accept
// or reject). Everything else — pending lists, the resolved strip, the
// per-row badges — is derived from these two maps plus the current step.
const LANDED_AT: Record<number, number> = { 1: 4, 2: 5, 3: 5, 4: 9, 5: 12, 6: 12 };
const RESOLVE_AT: Record<number, number> = { 1: 7, 2: 8, 3: 9, 4: 10, 5: 13, 6: 13 };

function landedAt(id: number): number {
  return LANDED_AT[id] ?? 1;
}
function statusOf(id: number, step: number): SugStatus {
  const at = RESOLVE_AT[id];
  if (at != null && step >= at) return id === 2 ? "rejected" : "accepted";
  return "pending";
}
function resolvingIds(step: number): number[] {
  return Object.entries(RESOLVE_AT)
    .filter(([, at]) => at === step)
    .map(([id]) => Number(id));
}
// the margin summary row carries two suggestions across the deck (#3, then
// #4) — at most one is ever "active" on it at a given step.
function marginBadgeInfo(step: number): { id: number; status: SugStatus } | null {
  if (step >= 5 && step <= 8) return { id: 3, status: "pending" };
  if (step === 9) return { id: 4, status: "pending" };
  return null;
}

type Step = { n: number; title: string; note: string };
const STEPS: Step[] = [
  { n: 1, title: "An empty job", note: "A plain spreadsheet. The queue is here, but there is nothing to review yet." },
  { n: 2, title: "Drop the brief", note: "The RFQ lands on the sheet — no message is typed." },
  { n: 3, title: "It gets to work", note: "No thinking box. The rows themselves show it is running." },
  { n: 4, title: "First suggestion", note: "Assembly labor rate, proposed — flagged right on its row." },
  { n: 5, title: "Two more land", note: "Materials pricing and a margin call join the queue — three cards now." },
  { n: 6, title: "Open card one", note: "Selecting a card highlights its row on the sheet, and back again." },
  { n: 7, title: "Accept it", note: "The cell updates. The card moves to the record, not the trash." },
  { n: 8, title: "Reject, with a reason", note: "One line, typed on the card — no message needed to say no." },
  { n: 9, title: "A new one lands", note: "The margin call settles; a fourth suggestion arrives on its own." },
  { n: 10, title: "Accept the bump", note: "The queue keeps watching mid-review — this one wasn't asked for." },
  { n: 11, title: "First pass, resolved", note: "Every card decided. Every accepted cell still carries its mark." },
  { n: 12, title: "The client pushes back", note: "The estimator edits the target price directly — not a suggestion." },
  { n: 13, title: "A second wave", note: "That edit triggers two new cards, worked through the same way." },
  { n: 14, title: "Never invisible", note: "Accepted, rejected, or reasoned about — every decision stays on record." },
];

export function OutformConceptThree({ width = 2440 }: { width?: number }) {
  const APP_W = 1680;
  const APP_H = 1000;
  const scale = width / APP_W;

  const [step, setStep] = useState(1);
  const [playing, setPlaying] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);

  const s = STEPS[step - 1];

  const advance = useCallback(() => {
    setStep((cur) => {
      if (cur >= STEPS.length) {
        setPlaying(false);
        return cur;
      }
      return cur + 1;
    });
  }, []);

  const go = useCallback((n: number) => setStep(Math.max(1, Math.min(STEPS.length, n))), []);

  // PLAY — a fixed timer, not audio. Same choice as the sibling concepts:
  // narration has never been confirmed heard, so this stays an honest
  // click-through and advances on a clock instead.
  const STEP_MS = 3400;
  useEffect(() => {
    if (!playing) return;
    const t = window.setInterval(() => advance(), STEP_MS);
    return () => window.clearInterval(t);
  }, [playing, advance]);

  // reveal-in animation — any element tagged `.oc3-at-N` fades/slides in when
  // the deck reaches step N. A resolving card (just accepted/rejected this
  // step) also gets a small settle-in pulse.
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const q = (sel: string) => Array.from(el.querySelectorAll(sel)) as HTMLElement[];
    const m = q(`.oc3-at-${step}`);
    if (m.length) animate(m, { opacity: [0, 1], y: [10, 0], duration: 400, delay: stagger(90) });
    const resolvingCards = q(".oc3-card.is-accepted, .oc3-card.is-rejected");
    if (resolvingCards.length) animate(resolvingCards, { scale: [0.97, 1], duration: 260 });
  }, [step]);

  const closing = step === 14;

  const landedSugs = SUGGESTIONS.filter((sg) => step >= landedAt(sg.id));
  const resolving = resolvingIds(step);
  const expanded = landedSugs.filter((sg) => statusOf(sg.id, step) === "pending" || resolving.includes(sg.id));
  const compact = landedSugs.filter((sg) => statusOf(sg.id, step) !== "pending" && !resolving.includes(sg.id));
  const pendingCount = landedSugs.filter((sg) => statusOf(sg.id, step) === "pending").length;
  const reviewingId = step === 6 ? 1 : null;

  const showTotals = step >= 4;
  const cost = showTotals ? totalCost(step) : 0;
  const price = showTotals ? computePrice(step) : 0;
  const margin = showTotals && price > 0 ? (price - cost) / price : 0;
  const mBadge = marginBadgeInfo(step);
  const marginHasComment = step >= 9;
  const priceEdited = step >= 12;

  return (
    <div style={{ width }}>
      <div className="nowheel nodrag nopan" style={{ width, height: APP_H * scale, overflow: "hidden", borderRadius: 14 }}>
        <div
          ref={root}
          className="oc3-root"
          style={{ width: APP_W, height: APP_H, transform: `scale(${scale})`, transformOrigin: "top left" }}
        >
          <div className="oc3-flag">
            Design prototype · illustrative numbers · draft concept for comparison, not the shipped UI · the
            spreadsheet below is a mock, not the real Univer sheet
          </div>

          {closing ? (
            <Closing />
          ) : (
            <div className="oc3-app" dir="ltr">
              <section className="oc3-sheetpane">
                <SheetHead step={step} />
                <div className="oc3-sheetbody">
                  {step >= 2 ? (
                    <div className="oc3-dropchip oc3-at-2">
                      <span className="oc3-file-ic">PDF</span>
                      <div>
                        <b>Nordstrom_Denim_Endcap_RFQ_v2.pdf</b>
                        <span>Dropped on the sheet · 340 units · ship mid-October</span>
                      </div>
                    </div>
                  ) : null}
                  {step === 3 ? (
                    <div className="oc3-workstrip oc3-at-3">
                      <span className="oc3-workstrip-dot" />
                      Reading the brief, pricing the line items…
                    </div>
                  ) : null}
                  <div className="oc3-grid">
                    {SHEET_ROWS.map((def, i) => (
                      <Row key={i} def={def} step={step} reviewingId={reviewingId} />
                    ))}
                    {showTotals ? (
                      <div className="oc3-summary">
                        <div className={`oc3-summary-row ${margin < MARGIN_FLOOR ? "is-warn" : ""}`}>
                          <span>Margin</span>
                          <b>{(margin * 100).toFixed(1)}%</b>
                          <em>{margin < MARGIN_FLOOR ? `under the ${Math.round(MARGIN_FLOOR * 100)}% floor` : "on target"}</em>
                          {mBadge && mBadge.status === "pending" ? <div className="oc3-num-badge">{mBadge.id}</div> : null}
                          {marginHasComment && !mBadge ? (
                            <div
                              className="oc3-comment-badge"
                              title="Margin target set from comparable jobs and accepted through the queue."
                            />
                          ) : null}
                        </div>
                        <div className={`oc3-summary-row ${step === 12 ? "is-flash" : ""}`}>
                          <span>Total price</span>
                          <b>{money(price)}</b>
                          <em>cost {money(cost)}</em>
                          {priceEdited ? <div className="oc3-edited-tag">Edited</div> : null}
                        </div>
                      </div>
                    ) : (
                      <div className="oc3-summary">
                        <div className="oc3-summary-row">
                          <span>Margin</span>
                          <b>—</b>
                          <em>—</em>
                        </div>
                        <div className="oc3-summary-row">
                          <span>Total price</span>
                          <b>—</b>
                          <em>—</em>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <section className="oc3-queuepane">
                <div className="oc3-queuehead">
                  <span className="oc3-queueicon">Q</span>
                  <div>
                    <b>Review queue</b>
                    <span>{pendingCount > 0 ? `${pendingCount} to review · accept or reject` : "No reply required to operate it"}</span>
                  </div>
                </div>
                <div className="oc3-queuelist">
                  {expanded.length === 0 && compact.length === 0 ? <QueueIdle step={step} /> : null}
                  {expanded.length === 0 && compact.length > 0 ? (
                    <div className="oc3-queue-clear">Queue clear — {compact.length} resolved below.</div>
                  ) : null}
                  {expanded.map((sg) => (
                    <SuggestionCard key={sg.id} s={sg} step={step} active={sg.id === reviewingId} />
                  ))}
                </div>
                <ResolvedStrip items={compact} step={step} />
              </section>
            </div>
          )}
        </div>
      </div>

      <div className="oc3-ctl nowheel nodrag nopan">
        <button onClick={() => go(step - 1)} disabled={step === 1}>
          ‹
        </button>
        <div className="oc3-ctl-dots">
          {STEPS.map((x) => (
            <button key={x.n} className={x.n === step ? "on" : ""} onClick={() => go(x.n)} title={x.title} />
          ))}
        </div>
        <button onClick={() => go(step + 1)} disabled={step === STEPS.length}>
          ›
        </button>
        <button className="oc3-play" onClick={() => setPlaying((p) => !p)}>
          {playing ? "Pause" : "Play"}
        </button>
        <div className="oc3-ctl-text">
          <b>
            {step} / {STEPS.length} — {s.title}
          </b>
          <span>{s.note}</span>
        </div>
      </div>
    </div>
  );
}

function SheetHead({ step }: { step: number }) {
  const known = step >= 2;
  return (
    <div className="oc3-sheethead">
      <div className="oc3-sheethead-title">
        <b>{known ? "Nordstrom · Denim Bay End-cap" : "New estimate"}</b>
        <em>{known ? "NB-2201_Estimate · 340 units" : "—"}</em>
      </div>
      {step >= 11 ? <span className="oc3-ready">First pass resolved</span> : null}
    </div>
  );
}

function Row({
  def,
  step,
  reviewingId,
}: {
  def: RowDef;
  step: number;
  reviewingId: number | null;
}) {
  if (def.kind === "colhead") {
    return (
      <div className="oc3-row oc3-row-head">
        <span className="oc3-c oc3-c-label">Item</span>
        <span className="oc3-c oc3-c-qty">Qty</span>
        <span className="oc3-c oc3-c-unit">Unit</span>
        <span className="oc3-c oc3-c-rate">Rate</span>
        <span className="oc3-c oc3-c-ext">Ext. cost</span>
      </div>
    );
  }
  if (def.kind === "section") {
    return (
      <div className="oc3-row oc3-row-section">
        <span className="oc3-c oc3-c-label">{def.label}</span>
      </div>
    );
  }

  const rate = rateFor(def.id, step);
  const ext = rate == null ? null : def.qty * rate;
  const sug = SUGGESTIONS.find((sg) => sg.rowId === def.id) ?? null;
  const status: SugStatus | null = sug && step >= landedAt(sug.id) ? statusOf(sug.id, step) : null;
  const reviewing = sug != null && sug.id === reviewingId;
  const shimmer = step === 3;

  const cls = [
    "oc3-row",
    shimmer && "is-shimmer",
    status === "pending" && "is-pending",
    reviewing && "is-reviewing",
    status === "accepted" && "is-accepted",
    status === "rejected" && "is-reviewed",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cls}>
      <span className="oc3-c oc3-c-label">
        {def.label}
        {sug && status === "pending" ? <span className="oc3-num-badge">{sug.id}</span> : null}
        {sug && status === "accepted" ? (
          <span className="oc3-comment-badge" title={`${sug.title}: ${sug.before} → ${sug.after}. ${sug.reason}`} />
        ) : null}
        {sug && status === "rejected" ? (
          <span className="oc3-reviewed-badge" title={`Reviewed, kept as ${sug.before}. ${sug.rejectReason ?? sug.reason}`} />
        ) : null}
      </span>
      <span className="oc3-c oc3-c-qty">{def.qty}</span>
      <span className="oc3-c oc3-c-unit">{def.unit}</span>
      <span className="oc3-c oc3-c-rate">{rate == null ? "—" : `$${rate.toFixed(2)}`}</span>
      <span className="oc3-c oc3-c-ext">{ext == null ? "—" : money(ext)}</span>
    </div>
  );
}

function QueueIdle({ step }: { step: number }) {
  const msg = step === 1 ? "Drop a brief onto the sheet to begin." : "Reading the brief — first suggestion is coming up.";
  return (
    <div className="oc3-queue-idle">
      <span className="oc3-queue-idle-dot" />
      <p style={{ margin: 0 }}>{msg}</p>
    </div>
  );
}

function SuggestionCard({ s, step, active }: { s: Suggestion; step: number; active: boolean }) {
  const status = statusOf(s.id, step);
  const cls = ["oc3-card", active && "is-active", status === "accepted" && "is-accepted", status === "rejected" && "is-rejected"]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={cls}>
      <div className="oc3-card-head">
        <span className="oc3-card-num">{s.id}</span>
        <b>{s.title}</b>
        {status === "accepted" ? <span className="oc3-card-tag is-accept">Accepted</span> : null}
        {status === "rejected" ? <span className="oc3-card-tag is-reject">Rejected</span> : null}
      </div>
      <div className="oc3-card-diff">
        <span className={`oc3-diff-before ${status === "rejected" ? "is-kept" : ""}`}>{s.before}</span>
        <span className="oc3-diff-arrow">→</span>
        <span className={`oc3-diff-after ${status === "rejected" ? "is-struck" : ""}`}>{s.after}</span>
      </div>
      <p className="oc3-card-reason">{s.reason}</p>
      {status === "rejected" && s.rejectReason ? (
        <div className="oc3-card-rejectnote">
          <span>Reason for rejecting</span>
          <p>{s.rejectReason}</p>
        </div>
      ) : null}
      {status === "pending" ? (
        <div className="oc3-card-actions">
          <button className="oc3-btn-accept">Accept</button>
          <button className="oc3-btn-reject">Reject</button>
          <button className="oc3-btn-ask">Ask about this</button>
        </div>
      ) : null}
    </div>
  );
}

function ResolvedStrip({ items, step }: { items: Suggestion[]; step: number }) {
  if (!items.length) return null;
  return (
    <div className="oc3-resolved">
      <div className="oc3-resolved-head">
        Resolved <b>{items.length}</b>
      </div>
      <div className="oc3-resolved-list">
        {items.map((s) => {
          const status = statusOf(s.id, step);
          return (
            <div
              key={s.id}
              className={`oc3-resolved-item ${status === "rejected" ? "is-reject" : "is-accept"}`}
              title={`${s.title}: ${s.before} → ${s.after}`}
            >
              <span className="oc3-resolved-num">{s.id}</span>
              <span className="oc3-resolved-title">{s.title}</span>
              <span className="oc3-resolved-mark">{status === "rejected" ? "✕" : "✓"}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Closing() {
  return (
    <div className="oc3-closing oc3-at-14">
      <div className="oc3-closing-card">
        <div className="oc3-closing-kicker">Outform — Concept 3 · Review Queue</div>
        <h2>Every suggestion is a decision on record — accepted, rejected, or reasoned about, never invisible.</h2>
        <p>
          The agent never holds a conversation. It works the document directly and drops what it is unsure about
          into a queue — a rate, a price, a margin call — each one tied to the cell it touches. Accept, reject, or
          ask; either way it is recorded against the job.
        </p>
        <div className="oc3-closing-split">
          <div>
            <b>Suggestion</b>
            <span>The agent's own idea. Numbered, tied to a cell, sitting in the queue until someone decides.</span>
          </div>
          <div>
            <b>Direct edit</b>
            <span>The estimator's own hand. Never a suggestion — and it's what tells the agent to look again.</span>
          </div>
        </div>
        <div className="oc3-closing-stats">
          <div>
            <b>5</b>
            <span>accepted</span>
          </div>
          <div>
            <b>1</b>
            <span>rejected</span>
          </div>
          <div>
            <b>6</b>
            <span>decisions on record</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OutformConceptThree;
